import React, { Component } from 'react';

import { ProductNotifyModal } from './asyncProductPageModals';

import { pluralize, stripSpecialChars } from 'helpers/index';
import MelodyCardProduct from 'components/common/melodyCard/MelodyCardProduct';
import MelodyModal from 'components/common/MelodyModal';
import ProductUtils from 'helpers/ProductUtils';
import { buildSeoProductUrl } from 'helpers/SeoUrlBuilder';
import type { FormattedProductBundle, FormattedProductSizing } from 'reducers/detail/productDetail';
import type { ProductStyle } from 'types/cloudCatalog';
import type { SelectedSizing } from 'types/product';
import { MartyContext } from 'utils/context';

import css from 'styles/components/productdetail/outOfStockModal.scss';

interface Props {
  detail: FormattedProductBundle;
  hasAlts: number | false | null; // TODO ts this is kinda gross, so let's refactor `hasAlts` inside `outofstockmodalwrapper` to just return a boolean
  makeSelectedSizing: (sizeId?: string) => SelectedSizing;
  onClose: () => void;
  onOpenProductNotifyMe: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onStyleChange: (event: React.MouseEvent) => void;
  productId: string;
  productStyles: Record<string, ProductStyle>;
  selectedSizing: SelectedSizing;
  sizing: FormattedProductSizing;
  style: ProductStyle;
  showUnifiedOOS: boolean;
  renderRecos?: () => React.ReactNode;
}

/**
 * Zappos out of stock modal
 */
class OutOfStockModal extends Component<Props> {
  makeAltOnComponentClick = () => {
    const {
      props: { onStyleChange }
    } = this;

    return (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      onStyleChange(e);
    };
  };

  makeAltList = (sizeId: string) => {
    const {
      props: { detail, makeSelectedSizing, productStyles, sizing },
      makeAltOnComponentClick
    } = this;
    const { stockData } = sizing;
    const newSelectedSizing = makeSelectedSizing(sizeId);
    const colorsBySize = ProductUtils.getColorsBySize(stockData, newSelectedSizing);
    // Render sizing values only if there are available colors in stock
    if (colorsBySize.length) {
      // Find styleIds that have a color match with colorsBySize, along with stock
      const matches = Object.keys(productStyles).filter(styleId => {
        const style = productStyles[styleId]!; // gets whole obj
        return colorsBySize.find(obj => obj.color === style.colorId && obj.onHand !== '0');
      });
      return (
        <>
          {matches.map(styleId => {
            const style = productStyles[styleId]!;
            const { colorId, price, images, color } = style;
            const mainImage = images.find(imgs => imgs.type === 'PAIR' || imgs.type === 'MAIN');
            const imageId = mainImage ? mainImage.imageId : null;
            const msaImageParams = { height: 83, width: 110, autoCrop: true };
            const seoUrl = buildSeoProductUrl(detail, colorId);
            const cardData = {
              alt: color,
              colorName: color,
              link: seoUrl,
              colorId,
              imageId,
              price,
              styleId
            };

            return (
              <MelodyCardProduct
                key={`${stripSpecialChars(imageId)}_${styleId}`}
                cardData={cardData}
                componentStyle="oos"
                data-style-id={styleId}
                melodyCardTestId="OOS"
                msaImageParams={msaImageParams}
                onComponentClick={makeAltOnComponentClick()}
              />
            );
          })}
        </>
      );
    } else {
      return null;
    }
  };

  makeAltCombinations = () => {
    const { selectedSizing, sizing } = this.props;
    const { dimensions } = sizing;
    if (!dimensions.length) {
      return null;
    }
    const { id, rank } = dimensions[0]!;
    const dimId = `d${id}`;
    let currentSizedId;

    if (rank === '1') {
      currentSizedId = selectedSizing[dimId];
    }

    const chosenSizeValue = currentSizedId && this.makeAltList(currentSizedId);
    if (chosenSizeValue) {
      const count = chosenSizeValue.props.children.length;
      return (
        <MartyContext.Consumer>
          {({ testId }) => (
            <>
              <p className={css.count}>
                View {count} available {pluralize('color', count)} in your size
              </p>
              <div className={css.otherColors} data-test-id={testId('otherStylesInSizeContainer')}>
                {chosenSizeValue}
              </div>
            </>
          )}
        </MartyContext.Consumer>
      );
    } else {
      return null;
    }
  };

  render() {
    const { onClose, detail, onOpenProductNotifyMe, hasAlts, showUnifiedOOS } = this.props;
    const altCombinations = hasAlts ? this.makeAltCombinations() : [];

    if (hasAlts && !showUnifiedOOS) {
      return (
        <MelodyModal
          isOpen={true}
          onRequestClose={onClose}
          className={css.modalContent}
          heading="Out of Stock"
          headingTestId="oosOtherStylesHeader"
          wrapperTestId="outOfStockOtherStyles"
          buttonTestId="close"
        >
          <MartyContext.Consumer>
            {({ testId }) => (
              <div className={css.body}>
                <div className={css.oosNotify}>
                  <button type="button" onClick={onOpenProductNotifyMe} data-test-id={testId('notifyMeOutOfStock')}>
                    Notify me when this is back in stock!
                  </button>
                </div>
                {this.makeAltCombinations()}
              </div>
            )}
          </MartyContext.Consumer>
        </MelodyModal>
      );
    } else {
      return (
        <ProductNotifyModal
          isOpen={true}
          onClose={onClose}
          product={detail}
          productId={this.props.productId}
          colorId={this.props.style.colorId}
          selectedSizing={this.props.selectedSizing}
          altCombinations={altCombinations}
          renderRecos={this.props.renderRecos}
        />
      );
    }
  }
}

export default OutOfStockModal;
